"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";

import { useEffect, useState } from "react";

import { useParams, useSearchParams } from "next/navigation";

import { AuthShell, AuthSurface } from "@/components/auth";
import { FormHeaderActionsProvider } from "@/components/form/form-header-actions-context";
import { MfaAuth, MfaAuthPanel } from "@/components/mfa";
import { PatientProfileBadge } from "@/components/patient/patient-profile-badge";

import { queryClient } from "@/config/react-query";

import { PatientFormFillProvider } from "@/contexts/form-fill-context";
import FormStateProvider from "@/contexts/form-provider";

import {
  usePatientClaim,
  useResetPatientClaim,
} from "@/hooks/patient-claim/use-patient-claim";
import { useSignOutPatient } from "@/hooks/patient-claim/use-sign-out-patient";

import { PatientClaimAuthMode } from "@/types/patient-claim";

import FormNotFound from "./form-not-found";
import { FormPage } from "./form-page-core";
import { formExists as apiFormExists } from "@/api/forms/form";

type FormPageWrapperProps = {
  formPath: string;
  redirectUrl?: string | null;
};

const FormPageWrapper = ({ formPath, redirectUrl }: FormPageWrapperProps) => {
  const { claimedPatient: patient, authMode } = usePatientClaim();
  const resetMfaState = useResetPatientClaim();
  const handleSignOut = useSignOutPatient();
  // Family members often share a phone, so a phone session can hold several
  // patients: clearing the pick and reloading brings the picker back.
  // Portal sessions belong to one patient, so switching means signing out.
  const handleSwitchPatient =
    authMode === PatientClaimAuthMode.PHONE
      ? () => {
          resetMfaState();
          window.location.reload();
        }
      : handleSignOut;
  if (!patient) {
    return (
      <MfaAuth surface={AuthSurface.FORMS}>
        <AuthShell tagline="Securing access to this form">
          <MfaAuthPanel tAndSFooterText="filling out this form" />
        </AuthShell>
      </MfaAuth>
    );
  }
  return (
    <FormHeaderActionsProvider
      actions={
        <PatientProfileBadge
          firstName={patient.firstName}
          lastName={patient.lastName}
          onSignOut={handleSignOut}
        />
      }
    >
      <FormPage
        formPath={formPath}
        patientId={patient.id}
        onReset={resetMfaState}
        onSwitchPatient={handleSwitchPatient}
        redirectUrl={redirectUrl}
      />
    </FormHeaderActionsProvider>
  );
};

const RootComponent = () => {
  const params = useParams();
  const searchParams = useSearchParams();
  const formPath = params.formPath as string;
  const redirectUrl = searchParams.get("redirectUrl");
  const membershipId = searchParams.get("membershipId");
  const [formExists, setFormExists] = useState(true);

  useEffect(() => {
    const checkFormExists = async () => {
      const data = await apiFormExists(formPath);
      if (!data) {
        setFormExists(false);
      }
    };
    checkFormExists();
  }, [formPath]);

  useEffect(() => {
    if (formExists) {
      posthog.startSessionRecording({ sampling: true });
    }
  }, [formExists]);

  if (!formExists) {
    return <FormNotFound />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <FormStateProvider>
        <PatientFormFillProvider membershipId={membershipId ?? undefined}>
          <FormPageWrapper formPath={formPath} redirectUrl={redirectUrl} />
        </PatientFormFillProvider>
      </FormStateProvider>
    </QueryClientProvider>
  );
};

export default RootComponent;
